import { useCallback, useEffect, useRef, useState } from 'react'

import type { PixiSlot } from './game/PixiSlot'
import type { PixiTumble } from './game/PixiTumble'
import { TURBO_TUMBLE_SPEED, useGameStore } from './store/gameStore'
import type { ResolvedTheme } from './theme'
import { BigWinOverlay } from './ui/BigWinOverlay'
import { BonusAura } from './ui/BonusAura'
import { ControlBar } from './ui/ControlBar'
import { CrestSpark } from './ui/CrestSpark'
import { GamblePanel } from './ui/GamblePanel'
import { Lobby } from './ui/Lobby'
import { LoginScreen } from './ui/LoginScreen'
import { MathBadge } from './ui/MathBadge'
import { MusicToggle } from './ui/MusicToggle'
import { ReelsCanvas } from './ui/ReelsCanvas'
import { StageCharacter } from './ui/StageCharacter'
import { StageEffects } from './ui/StageEffects'
import { RulesPanel } from './ui/RulesPanel'
import { TumbleCanvas } from './ui/TumbleCanvas'
import { gameFromUrl, onGameUrlChange, pushGameUrl } from './ui/route'

/**
 * Clickable parts of each character, by id inside its SVG.
 *
 * These are the SAME ids the art already animates on
 * (`begin="ghostBody.click"` and friends), so the sound and the visible
 * reaction cannot drift apart. Module constants, not inline literals: a
 * fresh array every render would re-run the listener effect each time.
 */
const DEVIL_HOTSPOTS = ['devilHead', 'devilTorso'] as const
const CAT_HOTSPOTS = ['hellcat'] as const
const GHOST_HOTSPOTS = ['ghostBody'] as const

export function App(): React.JSX.Element {
  const slotRef = useRef<PixiSlot | null>(null)
  const tumbleRef = useRef<PixiTumble | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const phase = useGameStore((s) => s.phase)
  const turbo = useGameStore((s) => s.turbo)
  const screen = useGameStore((s) => s.screen)
  const error = useGameStore((s) => s.error)
  const authMethod = useGameStore((s) => s.authMethod)
  const signOut = useGameStore((s) => s.signOut)
  const game = useGameStore((s) => s.game)
  const theme = useGameStore((s) => s.theme)
  const games = useGameStore((s) => s.games)
  const boot = useGameStore((s) => s.boot)
  const spin = useGameStore((s) => s.spin)
  const closeGame = useGameStore((s) => s.closeGame)
  const lastRound = useGameStore((s) => s.lastRound)
  const freeSpinsLeft = useGameStore((s) => s.freeSpinsLeft)
  const freeSpinIndex = useGameStore((s) => s.freeSpinIndex)
  const inFreeSpins = useGameStore((s) => s.inFreeSpins)
  const lastBombSum = useGameStore((s) => s.lastBombSum)
  const globalMult = useGameStore((s) => s.globalMult)
  const displayWin = useGameStore((s) => s.displayWin)
  const boundGameId = useGameStore((s) => s.boundGameId)
  const framed = useGameStore((s) => s.framed)

  useEffect(() => {
    void boot()
  }, [boot])

  // The tab is the only label a player sees when the game is in a background
  // tab or a bookmark, so it names the game they actually opened. Back in the
  // lobby there is no game, and the client is the showcase, not any one title.
  useEffect(() => {
    document.title = game ? `${game.name} — Slot Games` : 'Slot Games'
  }, [game])

  useEffect(() => {
    const handleFullscreenChange = (): void => {
      setIsFullscreen(document.fullscreenElement != null)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const handleFullscreen = useCallback(async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen()
      return
    }

    await document.documentElement.requestFullscreen()
  }, [])

  // The game from the URL opens as soon as the lobby has arrived: a link to
  // a demo must lead into that demo, not into the list.
  const openedFromUrl = useRef(false)
  useEffect(() => {
    // A bound session already opened its game in boot(), and ?game= must not
    // be able to talk us into a different one: the operator decided which
    // game this launch is for.
    if (games.length === 0 || openedFromUrl.current || boundGameId) return
    openedFromUrl.current = true
    const search = new URLSearchParams(window.location.search)
    // ?autospin without ?game is the old dev hook for screenshots: open the
    // server's default game so that it keeps working as before.
    const wanted =
      gameFromUrl() ??
      (search.has('autospin') ? useGameStore.getState().defaultGameId : null)
    if (wanted) void useGameStore.getState().openGame(wanted)
  }, [games, boundGameId])

  // Browser "back" button: the URL is the source of truth about the screen —
  // except in a bound session, which has exactly one screen and no lobby to
  // go back to.
  useEffect(() => {
    if (boundGameId) return
    return onGameUrlChange((gameId) => {
      const store = useGameStore.getState()
      if (gameId) void store.openGame(gameId)
      else store.closeGame()
    })
  }, [boundGameId])

  const handleOpenGame = useCallback((gameId: string) => {
    // pushState does not fire popstate, so open the game explicitly; the
    // subscription below is only needed for "back/forward".
    pushGameUrl(gameId)
    void useGameStore.getState().openGame(gameId)
  }, [])
  const handleBack = useCallback(() => {
    const store = useGameStore.getState()
    if (store.phase === 'spinning') return
    // In a bound session closeGame() only asks the operator to close the
    // iframe and leaves our screen where it is — so the URL must stay put
    // too, otherwise "back" would clear ?game= and change nothing else.
    if (!store.boundGameId) pushGameUrl(null)
    closeGame()
  }, [closeGame])

  // Dev hook for screenshots / autotests: ?autospin=buy spins once after
  // loading in the given mode. In production the URL parameter does nothing
  // without a dev session, because the spin still goes through the server.
  const autoSpun = useRef(false)
  useEffect(() => {
    if (phase !== 'idle' || !game || autoSpun.current) return
    const wanted = new URLSearchParams(window.location.search).get('autospin')
    if (!wanted) return
    autoSpun.current = true
    const mode = wanted === 'ante' || wanted === 'buy' ? wanted : 'base'
    useGameStore.getState().setMode(mode)
    // a short pause so the canvas has time to mount
    setTimeout(() => handleSpinRef.current(), 400)
  }, [phase, game])

  // Stable references: the canvases keep them in their effect dependencies,
  // and a fresh function on every render would re-create the canvas.
  const handleSlotReady = useCallback((slot: PixiSlot) => {
    slotRef.current = slot
  }, [])
  const handleTumbleReady = useCallback((board: PixiTumble) => {
    tumbleRef.current = board
  }, [])

  // The board takes the new rate the moment the toggle is pressed, not at
  // the next chain. A bonus is one round of ten-odd spins: a player who
  // flips turbo three spins in expects the fourth to be fast, and the store
  // alone would only pick it up between chains.
  useEffect(() => {
    tumbleRef.current?.setSpeed(turbo ? TURBO_TUMBLE_SPEED : 1)
  }, [turbo])

  const handleSpin = useCallback(() => {
    void spin({
      ways: async (window, opts) => {
        await slotRef.current?.spinTo(window, opts)
      },
      lines: async (lineWins, opts) => {
        await slotRef.current?.showLineWins(lineWins, opts)
      },
      tumble: async (s, cb, opts) => {
        tumbleRef.current?.setSpeed(opts?.speed ?? 1)
        await tumbleRef.current?.playSpin(s, cb)
      },
      banner: async (text, kind, opts) => {
        tumbleRef.current?.setSpeed(opts?.speed ?? 1)
        await tumbleRef.current?.banner(text, kind)
      },
    })
  }, [spin])
  const handleSpinRef = useRef(handleSpin)
  handleSpinRef.current = handleSpin

  // The mascot winks when a combination lands. SMIL inside an <img> is a
  // sealed box, so the mascot is mounted as an <object>: same standalone
  // animation, but contentDocument is reachable and the SVG's id="wink"
  // animation (begin="indefinite") can be fired from here. Once per round:
  // displayWin counts up during the reveal, and a wink per tick would turn
  // the devil into a blinker.
  const mascotRef = useRef<HTMLObjectElement | null>(null)
  const winked = useRef(false)
  useEffect(() => {
    if (displayWin <= 0) {
      winked.current = false
      return
    }
    if (winked.current) return
    winked.current = true
    const doc = mascotRef.current?.contentDocument
    const wink = doc?.getElementById('wink') as SVGAnimateElement | null
    wink?.beginElement?.()
  }, [displayWin])

  // Autospin loop. The store holds the counter; this effect fires the
  // actual spins because only App has the animators. Every auto round
  // starts here — including the first — so the count is honest: pick 10,
  // get 10. A short pause between rounds lets the result land visually.
  // A Big Win overlay pauses the loop: the next spin waits until the player
  // has dismissed the celebration, otherwise autospin steamrolls it.
  const autoSpinsLeft = useGameStore((s) => s.autoSpinsLeft)
  const celebrating = useGameStore((s) => s.celebrating)
  useEffect(() => {
    if (phase !== 'idle' || autoSpinsLeft <= 0 || screen !== 'game' || celebrating) return
    const timer = setTimeout(() => {
      const s = useGameStore.getState()
      if (s.phase !== 'idle' || s.autoSpinsLeft <= 0 || s.celebrating) return
      if (s.balanceCoins < s.chargeFor()) {
        s.stopAutoSpins()
        return
      }
      useGameStore.setState({ autoSpinsLeft: s.autoSpinsLeft - 1 })
      handleSpinRef.current()
    }, 500)
    return () => clearTimeout(timer)
  }, [phase, autoSpinsLeft, screen, celebrating])

  // Space is the hotkey players expect in a slot
  useEffect(() => {
    if (screen !== 'game') return
    const onKey = (e: KeyboardEvent): void => {
      if (e.code !== 'Space') return
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'].includes(target.tagName)) {
        return
      }
      e.preventDefault()
      handleSpin()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleSpin, screen])

  if (phase === 'boot') {
    return (
      <div className="app">
        <span className="subtitle">Loading…</span>
      </div>
    )
  }

  // No credentials and a server that runs real authentication. Before the
  // lobby, because a lobby of games that cannot be opened reads as a bug.
  if (phase === 'login') {
    return (
      <div className="app">
        <LoginScreen />
      </div>
    )
  }

  // A bound session never shows our lobby, not even for the frame between
  // boot finishing and the game opening: the operator's own lobby is the
  // one the player came from.
  if (screen === 'lobby' && boundGameId) {
    return (
      <div className="app">
        <span className="subtitle">Loading…</span>
      </div>
    )
  }

  if (screen === 'lobby') {
    return (
      <div className="app">
        {authMethod === 'password' && (
          <button className="signout" type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        )}
        <LobbyScreen onOpen={handleOpenGame} />
        {error && <div className="error">{error}</div>}
        <footer className="footer">
          The server decides the outcome. The client only draws what came back in the response.
        </footer>
      </div>
    )
  }

  const mechanic = game?.mechanic ?? 'ways'
  const isTumble = mechanic === 'tumble'
  const subtitle = game
    ? mechanic === 'tumble'
      ? `${game.layout.reels}×${game.layout.rows} · scatter pays ${game.payKeys[0]}+ · cascades`
      : mechanic === 'lines'
        ? `${game.layout.reels}×${game.layout.rows} · ${game.paylines?.length ?? 0} lines`
        : `${game.layout.rows ** game.layout.reels} ways`
    : ''

  return (
    // The game id doubles as a styling hook (`app--blazing-sevens`), so a
    // skin can restyle the chrome around the board without new plumbing.
    // `app--free-spins` opens the reserved gap under the crest. Without it
    // that strip is empty space between the crest and the cabinet; the badge
    // is the only thing that ever occupies it, so the layout follows the
    // badge rather than holding a hole open for it.
    //
    // `app--bonus` lights the aura around the board. It rides `inFreeSpins`,
    // not `freeSpinsLeft`, for the same reason the music does: the counter
    // survives past the summary banner, and the light has to come down WITH
    // the banner rather than linger into the next paid spin.
    <div
      className={
        `app${game ? ` app--${game.gameId}` : ''}` +
        (freeSpinsLeft > 0 ? ' app--free-spins' : '') +
        (inFreeSpins ? ' app--bonus' : '')
      }
    >
      {/* Fixed full-viewport backdrop: the page content grows and shrinks
          (gamble panel, badges), the scenery must not move with it. */}
      <div className="stage-bg" style={stageStyle(theme)} aria-hidden="true" />
      {/* Moving parts of the scenery — water, mist — over the still
          backdrop. Renders nothing unless the theme declares emitters. */}
      <StageEffects theme={theme} />
      <header className="header header--game">
        <div className="header-side header-side--left">
          {/* Three cases, one button. Our own lobby — go back to it. Bound and
              framed — ask the operator's lobby to close us. Bound but NOT
              framed (someone opened the launch URL directly) — there is
              nowhere to go, and a button that does nothing is worse than
              none. */}
          {(!boundGameId || framed) && (
            <button
              className="back header-action"
              onClick={handleBack}
              disabled={phase === 'spinning'}
              type="button"
            >
              {boundGameId ? '✕ Exit' : '‹ Back to games'}
            </button>
          )}
        </div>
        <div className="game-heading">
          <h1 className={`title${theme.header.logoImage ? ' title--art' : ''}`}>
            {theme.header.logoImage ? (
              <img
                className="title-logo"
                src={theme.header.logoImage}
                alt={game?.name ?? ''}
                // Without this the browser treats the crest as a file: a
                // drag lifts a translucent copy of the whole PNG and carries
                // it over the page, which reads as the header coming apart.
                draggable={false}
              />
            ) : (
              game?.name ?? '…'
            )}
          </h1>
          {theme.header.logoImage && <CrestSpark />}
          <span className="subtitle">
            {subtitle}
            {game ? ' · math ' : ''}
            {game && <MathBadge math={game.math} version={game.mathVersion} />}
          </span>
        </div>
        <div className="header-side header-side--right">
          {game && <RulesPanel game={game} />}
          <MusicToggle theme={theme} />
        </div>
      </header>

      {/* The branded crest deliberately stops before the board. This slot is
          reserved for the themed Buy Free Spins action, so adding it later
          cannot push the cabinet below the viewport or move the title. */}
      {theme.header.logoImage && <div className="header-feature-slot" aria-hidden="true" />}

      <div className="stage">
        {/* First in the stage, so the light paints behind the cabinet. */}
        {inFreeSpins && <BonusAura />}
        {game ? (
          isTumble ? (
            <TumbleCanvas game={game} theme={theme} onReady={handleTumbleReady} />
          ) : (
            <ReelsCanvas game={game} theme={theme} onReady={handleSlotReady} />
          )
        ) : (
          <div className="board board--empty">Loading game…</div>
        )}

        {freeSpinsLeft > 0 && (
          <div className="badge badge--free">
            Free Spin {freeSpinIndex} · {freeSpinsLeft} left
          </div>
        )}

        {lastBombSum > 0 && phase === 'spinning' && (
          <div className="badge badge--bomb">Multiplier ×{lastBombSum}</div>
        )}

        {/* Persistent games: the bonus-wide multiplier, visible for the whole bonus. */}
        {globalMult >= 0 && phase === 'spinning' && (
          <div className="badge badge--global">Total multiplier ×{globalMult}</div>
        )}

        {lastRound?.capped && phase === 'idle' && (
          <div className="badge badge--cap">Max win reached</div>
        )}

        {/* Decorative mascot and scenery from the theme. All are <object>
            rather than <img>: SMIL runs on its own either way, but only an
            object's inner document receives clicks — every character has a
            begin="<id>.click" reaction — and only the mascot's win-wink can
            be triggered from the effect above. */}
        {theme.stage.mascotImage && (
          <StageCharacter
            className="mascot"
            src={theme.stage.mascotImage}
            sfx="devil-hmm"
            hotspots={DEVIL_HOTSPOTS}
            objectRef={mascotRef}
          />
        )}
        {theme.stage.decorImage && (
          <StageCharacter
            className="decor"
            src={theme.stage.decorImage}
            sfx="cat-purr"
            hotspots={CAT_HOTSPOTS}
          />
        )}
        {/* Same file twice: the right copy is mirrored in CSS so the pair
            faces the board. Different voices, though — two identical "boo"s
            from two identical ghosts read as one sound with an echo. */}
        {theme.stage.ghostImage && (
          <>
            <StageCharacter
              className="ghost ghost--left"
              src={theme.stage.ghostImage}
              sfx="ghost-boo"
              hotspots={GHOST_HOTSPOTS}
            />
            <StageCharacter
              className="ghost ghost--right"
              src={theme.stage.ghostImage}
              sfx="ghost-woo"
              hotspots={GHOST_HOTSPOTS}
            />
          </>
        )}
      </div>

      <BigWinOverlay />

      <ControlBar onSpin={handleSpin} />

      {document.fullscreenEnabled && (
        <div className="fullscreen-row">
          <button
            className="fullscreen-toggle"
            type="button"
            onClick={() => void handleFullscreen()}
            aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
          >
            <span className="fullscreen-toggle__icon" aria-hidden="true">
              {isFullscreen ? '↙' : '↗'}
            </span>
            {isFullscreen ? 'Exit full screen' : 'Full screen'}
          </button>
        </div>
      )}

      {/* Risk Game — lines games with gamble enabled; the offer itself comes from the server. */}
      {game?.gamble && <GamblePanel game={game} />}

      {error && <div className="error">{error}</div>}

      <footer className="footer">
        The server decides the outcome. The client only draws what came back in the response.
      </footer>
    </div>
  )
}

/**
 * Page dressing from the theme, applied to the fixed `.stage-bg` layer.
 *
 * The area around the board is CSS, not WebGL: it never animates, and a
 * full-screen sprite would cost fill rate every frame for a picture that
 * never changes. The layer is position:fixed over the whole viewport, so
 * the backdrop is ALWAYS full-screen — panels appearing below the board
 * (gamble, badges) change the page height without touching it.
 */
function stageStyle(theme: ResolvedTheme): React.CSSProperties {
  const style: React.CSSProperties = {}
  if (theme.stage.background != null) {
    style.backgroundColor = `#${theme.stage.background.toString(16).padStart(6, '0')}`
  }
  if (theme.stage.backgroundImage) {
    // Layered over the colour, so while the image loads the player sees the
    // theme's backdrop rather than the stock page.
    style.backgroundImage = `url("${theme.stage.backgroundImage}")`
  }
  return style
}

/** An empty lobby is its own state: otherwise it would look like a hang. */
function LobbyScreen({ onOpen }: { onOpen: (gameId: string) => void }): React.JSX.Element {
  const games = useGameStore((s) => s.games)
  if (games.length === 0) {
    return <span className="subtitle">The server returned no games</span>
  }
  return <Lobby onOpen={onOpen} />
}
